<template>
    <vx-modal :padding="false" v-model:visible="visible" size="md">
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">{{ isEditing ? 'Update' : 'New' }} Category</div>
        </template>

        <div class="p-6">
            <div class="mb-3">
                <vx-label :id="colorLabelId">Color</vx-label>
                <thread-category-color-picker v-model="formData.color" :aria-labelledby="colorLabelId" />
            </div>
            <div class="mb-3">
                <vx-label>Name</vx-label>
                <vx-input
                    data-test="name-input"
                    v-model="formData.name"
                    size="lg"
                    :error="displayFirstError(validation.name)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Description (optional)</vx-label>
                <vx-input
                    data-test="description-input"
                    v-model="formData.description"
                    size="lg"
                    :rows="3"
                    :error="displayFirstError(validation.description)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Classification Instructions</vx-label>
                <vx-input
                    data-test="instructions-input"
                    v-model="formData.classification_instructions"
                    size="lg"
                    :rows="4"
                    placeholder="Describe what makes a conversation belong to this category..."
                    :error="displayFirstError(validation.classification_instructions)"
                >
                    <template v-slot:hint> These instructions guide the AI in classifying conversations. </template>
                </vx-input>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" :disabled="processing" @click="visible = false"> Cancel </vx-button>
                <vx-button
                    data-test="submit-btn"
                    class="grow"
                    color="primary"
                    size="lg"
                    type="button"
                    :loading="processing"
                    @click.prevent="submit()"
                >
                    {{ isEditing ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { required, maxLength } from '@vuelidate/validators';
import { computed, ref, useId, watch } from 'vue';
import ThreadCategoryColorPicker from './ThreadCategoryColorPicker.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { displayFirstError, groupAndOrgUnitName } from '~/utils/validation';
import { THREAD_CATEGORY_COLORS } from '~/constants/messageHub';
import type {
    ThreadCategoryFormData,
    ThreadCategoryType,
} from '~/services/intelligentInboxSettings/intelligentInboxSettingsClient.types';

const props = defineProps<{
    category: ThreadCategoryType | null;
    editIndex: number | null;
}>();

const emit = defineEmits<{
    saved: [data: ThreadCategoryFormData, editIndex: number | null];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const isEditing = computed(() => null !== props.editIndex);

const createEmptyFormData = (): ThreadCategoryFormData => ({
    name: '',
    description: null,
    classification_instructions: '',
    color: THREAD_CATEGORY_COLORS.sky,
});

const formData = ref<ThreadCategoryFormData>(createEmptyFormData());

const colorLabelId = useId();

watch(visible, (isVisible) => {
    if (isVisible) {
        if (props.category) {
            formData.value = {
                name: props.category.name,
                description: props.category.description,
                classification_instructions: props.category.classification_instructions,
                color: props.category.color ?? THREAD_CATEGORY_COLORS.sky,
            };
        } else {
            formData.value = createEmptyFormData();
        }
    } else {
        validation.value.$reset();
    }
});

const { submit, processing, validation } = useFormSubmission({
    data: formData,
    validation: {
        name: { required, maxLength: maxLength(80), groupAndOrgUnitName },
        description: { maxLength: maxLength(280) },
        classification_instructions: { required, maxLength: maxLength(3000) },
    },
    submit: async () => {
        emit('saved', { ...formData.value }, props.editIndex);
    },
    preserveScroll: true,
});
</script>
